import { z } from 'zod'
import { uuidSchema } from './common'

// Rating constants
export const FEEDBACK_RATINGS = {
  THUMBS_DOWN: -1,
  THUMBS_UP: 1
} as const

// Feedback schemas
export const feedbackSchema = z.object({
  id: uuidSchema,
  messageId: uuidSchema,
  userId: uuidSchema,
  rating: z.literal(FEEDBACK_RATINGS.THUMBS_DOWN).or(z.literal(FEEDBACK_RATINGS.THUMBS_UP)),
  comment: z.string().max(1000).nullable(),
  createdAt: z.date()
})

export const createFeedbackSchema = z.object({
  messageId: uuidSchema,
  rating: z.literal(FEEDBACK_RATINGS.THUMBS_DOWN).or(z.literal(FEEDBACK_RATINGS.THUMBS_UP)),
  comment: z.string().max(1000).optional()
})

export const updateFeedbackSchema = z.object({
  rating: z.literal(FEEDBACK_RATINGS.THUMBS_DOWN).or(z.literal(FEEDBACK_RATINGS.THUMBS_UP)).optional(),
  comment: z.string().max(1000).nullable().optional()
})

export const feedbackQuerySchema = z.object({
  messageId: uuidSchema.optional(),
  userId: uuidSchema.optional(),
  rating: z.literal(FEEDBACK_RATINGS.THUMBS_DOWN).or(z.literal(FEEDBACK_RATINGS.THUMBS_UP)).optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  agentType: z.enum(['HR', 'OPS']).optional()
})

export const feedbackStatsSchema = z.object({
  positive: z.number(),
  negative: z.number(),
  total: z.number(),
  positivePercentage: z.number()
})

// Extended schemas for admin dashboard
export const feedbackWithDetailsSchema = z.object({
  id: uuidSchema,
  messageId: uuidSchema,
  userId: uuidSchema,
  rating: z.literal(FEEDBACK_RATINGS.THUMBS_DOWN).or(z.literal(FEEDBACK_RATINGS.THUMBS_UP)),
  comment: z.string().nullable(),
  createdAt: z.date(),
  message: z.object({
    id: uuidSchema,
    content: z.string(),
    role: z.enum(['USER', 'ASSISTANT', 'SYSTEM']),
    confidence: z.number().nullable(),
    session: z.object({
      id: uuidSchema,
      agentType: z.enum(['HR', 'OPS']),
      title: z.string().nullable()
    }).optional()
  }),
  user: z.object({
    id: uuidSchema,
    firstName: z.string(),
    lastName: z.string()
  })
})

export const feedbackAnalyticsSchema = z.object({
  totalFeedback: z.number(),
  hrFeedback: feedbackStatsSchema,
  opsFeedback: feedbackStatsSchema,
  recentFeedback: z.array(feedbackWithDetailsSchema),
  negativeFeedback: z.array(feedbackWithDetailsSchema),
  trendsData: z.array(z.object({
    date: z.string(),
    positive: z.number(),
    negative: z.number(),
    total: z.number()
  }))
})

// Type exports
export type Feedback = z.infer<typeof feedbackSchema>
export type CreateFeedback = z.infer<typeof createFeedbackSchema>
export type UpdateFeedback = z.infer<typeof updateFeedbackSchema>
export type FeedbackQuery = z.infer<typeof feedbackQuerySchema>
export type FeedbackStats = z.infer<typeof feedbackStatsSchema>
export type FeedbackWithDetails = z.infer<typeof feedbackWithDetailsSchema>
export type FeedbackAnalytics = z.infer<typeof feedbackAnalyticsSchema>

// Rating type for easier use
export type FeedbackRating = typeof FEEDBACK_RATINGS[keyof typeof FEEDBACK_RATINGS] 